import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://zenithwellnesss.com";
const BRAND = "Zenith Wellness";

/**
 * Per-page SEO: title, description, canonical, OG, Twitter, optional JSON-LD.
 * Keep titles ~60 chars, descriptions ~155 chars.
 */
export default function Seo({
    title,
    description,
    path = "",
    image = "/zenith-logo.png",
    keywords,
    schema, // optional JSON-LD object or array
    noindex = false,
    exactTitle = false, // when true, do NOT append brand suffix
}) {
    const url = `${SITE}${path || "/"}`;
    const fullTitle = exactTitle || title?.includes(BRAND) ? title : `${title} | ${BRAND}`;
    const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

    return (
        <Helmet>
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={BRAND} />
            <meta property="og:locale" content="en_US" />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            {description && <meta property="og:description" content={description} />}
            <meta property="og:image" content={image.startsWith("http") ? image : `${SITE}${image}`} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            {description && <meta name="twitter:description" content={description} />}
            <meta name="twitter:image" content={image.startsWith("http") ? image : `${SITE}${image}`} />

            {schemas.map((s, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(s)}
                </script>
            ))}
        </Helmet>
    );
}

// Reusable JSON-LD schema builders
export const serviceSchema = (s) => ({
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: `${s.name} in San Diego`,
    description: s.description,
    procedureType: "https://schema.org/TherapeuticProcedure",
    bodyLocation: s.category,
    offers: {
        "@type": "Offer",
        price: s.price,
        priceCurrency: "USD",
        url: `${SITE}/services/${s.slug}`,
        availability: "https://schema.org/InStock",
    },
    provider: { "@id": `${SITE}/#localbusiness` },
});

export const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
});

export const breadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: `${SITE}${it.path}`,
    })),
});
